import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Send, Trophy, TrendingUp, CheckCircle2, Clock,
  XCircle, Target, AlertCircle, RefreshCw, BarChart2,
  ChevronUp, ChevronDown, Minus, DollarSign, Zap, Users, Download,
  Lock, Sparkles, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Premium Lock Overlay */
const PremiumLock = ({ price, onUpgrade, loading }) => (
  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center z-10 gap-3">
    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
      <Lock className="w-5 h-5 text-white" />
    </div>
    <div className="text-center">
      <p className="font-bold text-gray-900 text-sm">Premium Analitik</p>
      <p className="text-xs text-gray-500 mt-0.5">Bu bölüm Analytics Premium gerektirir</p>
    </div>
    <Button onClick={onUpgrade} disabled={loading} size="sm"
      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2 font-bold">
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      ${price}/ay ile Aç
    </Button>
  </div>
);

const STATUS_CONFIG = {
  accepted: { label: 'Won',      color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  pending:  { label: 'Pending',  color: 'bg-blue-100 text-blue-700 border-blue-200',           dot: 'bg-blue-500' },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-600 border-red-200',              dot: 'bg-red-400' },
};

const PIE_COLORS = ['#10b981', '#3b82f6', '#ef4444', '#f59e0b'];

const fmtAmount = (v) => {
  if (!v) return '—';
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000)    return `${(v / 1000).toFixed(0)}K`;
  return String(v);
};

const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-bold text-gray-800 mb-1 max-w-[160px] truncate">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">{p.name}: {typeof p.value === 'number' && p.value > 1000 ? p.value.toLocaleString() : p.value}</p>
      ))}
    </div>
  );
};

export default function ContractorAnalytics() {
  const { token } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [sortKey, setSortKey] = useState('submitted_at');
  const [sortDir, setSortDir] = useState('desc');
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const [dataRes, premiumRes] = await Promise.allSettled([
        axios.get(`${API}/contractor/proposal-analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contractor/analytics-premium-status`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (dataRes.status === 'fulfilled') setData(dataRes.value.data);
      if (premiumRes.status === 'fulfilled') setPremiumStatus(premiumRes.value.data);
    } catch {
      setError('Analytics yüklenemedi. Lütfen tekrar deneyin.');
    } finally { setLoading(false); }
  };

  const handleUpgradePremium = async () => {
    setPremiumLoading(true);
    try {
      const res = await axios.post(`${API}/billing/analytics-premium`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ödeme başlatılamadı');
    } finally { setPremiumLoading(false); }
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const exportCSV = () => {
    if (!proposals?.length) return;
    const headers = ['Brief / Show', 'City', 'Country', 'Amount', 'Currency', 'Status', 'Date Submitted'];
    const rows = proposals.map(p => [
      `"${p.brief_title || ''}"`, p.city || '', p.country || '',
      p.amount || '', p.currency || 'EUR',
      p.status || '', p.submitted_at ? new Date(p.submitted_at).toLocaleDateString('en-GB') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `proposals-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      <p className="text-sm text-gray-500">Analytics yükleniyor...</p>
    </div>
  );
  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <AlertCircle className="w-10 h-10 text-red-400" />
      <p className="text-sm text-red-600">{error}</p>
      <button onClick={fetchData} className="text-xs text-green-600 hover:underline flex items-center gap-1">
        <RefreshCw className="w-3.5 h-3.5" /> Yeniden Dene
      </button>
    </div>
  );

  const { summary, proposals, monthly_trend } = data || { summary: {}, proposals: [], monthly_trend: [] };

  const statusPieData = [
    { name: 'Won',      value: summary.won || 0 },
    { name: 'Pending',  value: summary.pending || 0 },
    { name: 'Declined', value: summary.declined || 0 },
  ].filter(d => d.value > 0);

  const sortedProposals = [...proposals].sort((a, b) => {
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <Minus className="w-3 h-3 text-gray-300 inline ml-1" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-green-500 inline ml-1" /> : <ChevronDown className="w-3 h-3 text-green-500 inline ml-1" />;
  };

  const topWin = proposals.find(p => p.is_winning);
  const bestRate = proposals.length > 0 ? (summary.won || 0) : 0;

  return (
    <div className="space-y-6" data-testid="contractor-analytics">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-green-600" />
            Proposal Analytics
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Track your proposal performance and win rate</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={fetchData} data-testid="refresh-analytics"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={exportCSV} disabled={!proposals?.length} data-testid="export-csv"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Analytics Premium Banner */}
      {premiumStatus && !premiumStatus.is_premium && premiumStatus.is_active && (
        <div data-testid="analytics-premium-banner" className="flex flex-wrap sm:flex-nowrap items-center gap-4 rounded-2xl border-2 border-dashed border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">Analytics Premium — Gelişmiş Raporlar</p>
            <p className="text-xs text-gray-500">Rakip analizi, ülke bazlı kazanma oranı, gelir tahmini ve daha fazlası</p>
          </div>
          <Button onClick={handleUpgradePremium} disabled={premiumLoading} data-testid="upgrade-analytics-btn"
            className="shrink-0 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2 font-bold">
            {premiumLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            ${premiumStatus.price_usd}/ay ile Aç
          </Button>
        </div>
      )}
      {premiumStatus?.is_premium && (
        <div data-testid="analytics-premium-active" className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
          <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-xs font-semibold text-amber-800">Analytics Premium aktif — bitiş: {new Date(premiumStatus.premium_until).toLocaleDateString('tr-TR')}</p>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Send}         label="Total Sent"       value={summary.total_proposals ?? 0}  sub="proposals submitted"           color="bg-slate-500" />
        <KpiCard icon={Trophy}       label="Won"              value={summary.won ?? 0}              sub={`Win rate: ${summary.win_rate ?? 0}%`} color="bg-emerald-500" />
        <KpiCard icon={Clock}        label="Pending"          value={summary.pending ?? 0}          sub="awaiting decision"             color="bg-blue-500" />
        <KpiCard icon={DollarSign}   label="Avg Win Amount"   value={fmtAmount(summary.avg_win_amount)} sub={`Avg sent: ${fmtAmount(summary.avg_sent_amount)}`} color="bg-violet-500" />
      </div>

      {/* Win Rate Banner */}
      {summary.total_proposals > 0 && (
        <Card className={`border-0 shadow-sm ${summary.win_rate >= 30 ? 'bg-emerald-50 border-l-4 border-l-emerald-400' : summary.win_rate >= 15 ? 'bg-amber-50 border-l-4 border-l-amber-400' : 'bg-slate-50 border-l-4 border-l-slate-300'}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${summary.win_rate >= 30 ? 'bg-emerald-500' : summary.win_rate >= 15 ? 'bg-amber-500' : 'bg-slate-400'}`}>
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-gray-800">
                {summary.win_rate >= 30 ? 'Excellent Win Rate!' : summary.win_rate >= 15 ? 'Good Win Rate' : 'Win Rate Needs Improvement'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {summary.win_rate >= 30 ? `You win ${summary.win_rate}% of proposals — platform avg is ~15%. Keep it up!`
                 : summary.win_rate >= 15 ? `Your ${summary.win_rate}% win rate is on track. Try competitive pricing on high-value briefs.`
                 : `Your ${summary.win_rate}% win rate is below average. Review your pricing and add portfolio photos to stand out.`}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-3xl font-black text-gray-900">{summary.win_rate}%</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Win Rate</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend — Premium */}
        <Card className="border-0 shadow-sm relative overflow-hidden">
          {!premiumStatus?.is_premium && (
            <PremiumLock price={premiumStatus?.price_usd || 49} onUpgrade={handleUpgradePremium} loading={premiumLoading} />
          )}
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-500" />6-Month Proposal Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-4">
            {monthly_trend.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-xs text-gray-400">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={monthly_trend} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="sent" fill="#93c5fd" name="Sent" radius={[3,3,0,0]} />
                  <Bar dataKey="won"  fill="#10b981" name="Won"  radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <div className="flex items-center gap-4 px-4 mt-1">
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-blue-300" /><span className="text-xs text-gray-500">Sent</span></div>
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /><span className="text-xs text-gray-500">Won</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Status Pie */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Target className="w-4 h-4 text-violet-500" />Proposal Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4 flex items-center justify-center">
            {statusPieData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-xs text-gray-400">No proposals yet</div>
            ) : (
              <div className="flex items-center gap-4 w-full">
                <ResponsiveContainer width="55%" height={180}>
                  <PieChart>
                    <Pie data={statusPieData} innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                      {statusPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-2.5">
                  {statusPieData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i] }} />
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{d.name}</p>
                        <p className="text-[10px] text-gray-400">{d.value} proposals</p>
                      </div>
                    </div>
                  ))}
                  {summary.total_proposals > 0 && (
                    <div className="mt-1 pt-2 border-t border-gray-100">
                      <p className="text-lg font-black text-emerald-600">{summary.win_rate}%</p>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">Win Rate</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Proposal Table */}
      <Card className="border-0 shadow-sm" data-testid="proposals-table">
        <CardHeader className="pb-3 pt-4 px-5">
          <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
            <Send className="w-4 h-4 text-green-500" />All Proposals
            <Badge className="ml-auto text-xs bg-green-50 text-green-700 border-green-200 font-medium">
              {proposals.length} total
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {proposals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
              <Send className="w-10 h-10" />
              <p className="text-sm font-medium">Henüz proposal gönderilmedi</p>
              <p className="text-xs">Brief Board'dan brief bularak teklif gönderebilirsin</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Brief</th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-green-600"
                      onClick={() => handleSort('status')}>
                      Status <SortIcon col="status" />
                    </th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-green-600 hidden md:table-cell"
                      onClick={() => handleSort('my_amount')}>
                      My Amount <SortIcon col="my_amount" />
                    </th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
                      Competition
                    </th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell cursor-pointer hover:text-green-600"
                      onClick={() => handleSort('submitted_at')}>
                      Date <SortIcon col="submitted_at" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProposals.map((p) => {
                    const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.pending;
                    const competScore = p.total_competitors > 6 ? 'High' : p.total_competitors > 3 ? 'Medium' : 'Low';
                    const competColor = competScore === 'High' ? 'text-red-500' : competScore === 'Medium' ? 'text-amber-500' : 'text-emerald-500';
                    return (
                      <tr key={p.brief_id} data-testid={`proposal-row-${p.brief_id}`}
                        className="border-b border-gray-50 hover:bg-green-50/30 transition-colors">
                        <td className="py-3.5 px-5">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`} />
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-800 text-sm leading-tight truncate max-w-[220px]">{p.title}</p>
                              <p className="text-[10px] text-gray-400 mt-0.5">{p.currency}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-3 text-center">
                          <Badge className={`text-[10px] px-2 py-0.5 border ${sc.color}`}>{sc.label}</Badge>
                          {p.is_winning && <Trophy className="w-3.5 h-3.5 text-amber-500 inline ml-1" />}
                        </td>
                        <td className="py-3.5 px-3 text-center hidden md:table-cell">
                          <span className="font-bold text-gray-800">{fmtAmount(p.my_amount)} {p.currency}</span>
                          {p.target_budget_max > 0 && (
                            <p className="text-[10px] text-gray-400">budget: {fmtAmount(p.target_budget_max)}</p>
                          )}
                        </td>
                        <td className="py-3.5 px-3 text-center hidden lg:table-cell">
                          <div className={`text-xs font-bold ${competColor}`}>
                            {competScore}
                          </div>
                          <p className="text-[10px] text-gray-400">{p.total_competitors} proposals</p>
                        </td>
                        <td className="py-3.5 px-3 text-center hidden lg:table-cell">
                          <span className="text-xs text-gray-500">
                            {p.submitted_at ? new Date(p.submitted_at).toLocaleDateString('tr-TR') : '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
